/**
 * `ChatStateController` — Lit ReactiveController die het reducer-pattern
 * implementeert + alle side-effects orchestreert.
 *
 * ## Lifecycle
 *
 *   1. **`hostConnected`** — registreert event-listeners (online/offline,
 *      visibilitychange, storage), kickt `bootstrap()` af.
 *   2. **`bootstrap`** — eenmalige fetch van `/service/status`, daarna
 *      hydrate uit localStorage als er een lopende of geëindigde sessie is.
 *   3. **`dispatch(action)`** — runs reducer (puur), schedulet host-render
 *      via `requestUpdate`, en routeert side-effects via `handleSideEffects`.
 *   4. **`hostDisconnected`** — cleared alle timers + event-listeners.
 *
 * ## Side-effect-architectuur
 *
 * `handleSideEffects` is een dunne router die delegeert naar 8 benoemde
 * reactor-methoden, elk met een single concern:
 *
 *   - `reactToAssistantSearchTransition`  — 1.2s delay vóór session-start
 *   - `reactToPanelOpenTransition`        — immediate fetch op panel-open
 *   - `reactToSessionRequest`             — POST /anonymous/new
 *   - `reactToPollingSwap`                — conv vs status mutually-exclusive
 *   - `reactToPersistence`                — localStorage snapshots
 *   - `reactToCustomerEndedConversation`  — POST /leave (sendBeacon-first)
 *   - `reactToMessageDispatch`            — POST per message + retry
 *   - `reactToSessionLost`                — auto SESSION_RESET fallback
 *
 * ## Polling-strategie
 *
 * Twee setTimeout-recursive pollers, mutually exclusive:
 *
 *   - **Conv-poller** (2s actief / 6s idle): tijdens queued + active.
 *     Adaptive: na ≥60s zonder merkbare verandering daalt naar 6s om
 *     server-load te besparen.
 *   - **Status-poller** (5s open / 30s dicht): tijdens idle, leest
 *     `/service/status` zodat we online/offline-transities oppikken.
 *
 * Tab-visibility: beide pollers gepauzeerd in background. Resume bij
 * comeback met immediate fetch.
 *
 * ## Persistence
 *
 * Snapshot in localStorage zodat refresh tijdens active OF ended sessie
 * de volledige conversatie + UI-state herstelt zonder server-roundtrip.
 * Wordt alleen gewist bij expliciete reset (visitor-side) of
 * server-confirmed loss.
 */
import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { ChatApiError, type ChatApiService } from '../api/service';
import { fetchIdentity, type FetchIdentityOptions } from '../api/identity';
import { fetchActiveContracts } from '../api/contracts';
import {
  endAavaConversation,
  getAavaConversationStatus,
  sendAavaMessage,
  startAavaConversation,
  type AavaPolledMessage,
} from '../api/aava';
// Note: `getAavaConversation` blijft beschikbaar in `src/api/aava.ts`
// voor toekomstige cross-device-sync of corrupted-storage-recovery.
// Huidige restore-flow gebruikt localStorage als bron-van-waarheid.
import { selectSelectedUnitId } from './selectors';
import { stripTrailingSlash } from '../utils/url';
import { chatReducer, initialState } from './reducer';
import {
  PollingScheduler,
  type PollingSchedulerConfig,
} from './polling-scheduler';
import {
  SESSION_KEY,
  VISITOR_KEY,
  clearAavaSession,
  clearDraft,
  clearPersistedSession,
  clearPersistedVisitor,
  loadAavaSession,
  clearPreSession,
  loadPanelOpenPref,
  loadPersistedSession,
  loadPreSession,
  savePreSession,
  loadPersistedVisitor,
  parsePersistedSession,
  parsePersistedVisitor,
  saveAavaSession,
  savePersistedSession,
  savePersistedVisitor,
  toPersistedMessage,
  updatePersistedActiveSnapshot,
  updatePersistedAssistantMessages,
  updatePersistedEndedSnapshot,
  updatePersistedLastReadAt,
  updatePersistedPanelOpen,
  type PersistedAavaSession,
} from './storage';
import type {
  ChannelId,
  ChatAction,
  ChatState,
  PendingMessage,
  SessionError,
  Visitor,
} from './types';

export class ChatStateController implements ReactiveController {
  private readonly host: ReactiveControllerHost;
  private readonly api: ChatApiService;
  private state: ChatState = initialState;

  /**
   * Polling-orchestratie: twee mutually-exclusive setTimeout-pollers
   * met adaptieve interval-keuze, panel/visibility/activity-aware.
   * Zie src/state/polling-scheduler.ts voor de scheduling-logica.
   */
  private readonly scheduler: PollingScheduler;

  /**
   * Timer voor de 1.2s vertraging tussen "ik zoek nu een medewerker" en
   * de echte session-creation. Tracked zodat we 'm kunnen cancellen op
   * unmount of SESSION_RESET — anders kan 'm in een verkeerde state vuren.
   */
  private intakeSubmitTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Pauze tussen `ASSISTANT_OPERATOR_FOUND` (bot kondigt aan) en
   * `SESSION_AGENT_ATTACHED` (echte phase-switch). 1200ms = ~800ms typing-
   * indicator + 400ms lees-tijd voor het bot-bericht zelf.
   */
  private static readonly ATTACH_REVEAL_DELAY_MS = 1200;

  /**
   * Tracked zodat we niet bij elke poll-tick (binnen het 1200ms-venster)
   * een nieuwe staged-attach starten — operator zit immers nog in de
   * polling-response zolang we niet ATTACHED hebben gedispatcht.
   */
  private attachRevealHandle: number | null = null;

  /**
   * Identity-flow config. Drie samenhangende properties — `url` (waar
   * vragen we?), `fetch` (welke fetcher? — fake voor tests) en `options`
   * (timeout etc.) — gegroepeerd zodat ze als één concern doorgegeven
   * worden i.p.v. los uit de controller-constructor te vallen.
   *
   * `url: null` = identity-flow uitgeschakeld; alle channel-picks
   * vallen direct in het anonieme pad. Stond bewust niet in het
   * `ChatApiService`-interface: identity is geen chat-API-call maar
   * een portal-roundtrip met andere CORS- en credential-semantiek.
   */
  private readonly identity: {
    url: string | null;
    fetch: typeof fetchIdentity;
    options: FetchIdentityOptions;
  };

  /**
   * Het enige kanaal als de host single-channel-config gebruikt. In dat
   * geval is er geen channel-picker UI, dus we dispatchen automatisch
   * `CHANNEL_PICKED` bij bootstrap én na `SESSION_RESET` zodat de
   * identity-flow ook in deze configuratie zijn natuurlijke trigger
   * heeft. `null` = multi-channel of geen channels-config.
   */
  private readonly defaultChannel: ChannelId | null;

  /**
   * AbortController voor in-flight identity-fetch. Gecanceld op
   * `hostDisconnected` zodat een navigeerende bezoeker geen netwerk-
   * verkeer (en geen dispatches op een dode controller) achterlaat.
   */
  private identityAbort: AbortController | null = null;

  /**
   * Base-URL voor de Embrace Customers REST API (`/v1/contract/active/me/paged`).
   * `null` = geen contract-fetch in de AVAA-flow (POC-fallback, voor
   * sites zonder Customers-API-toegang of voor de stub-mode tijdens dev).
   */
  private readonly customersApiBaseUrl: string | null;
  /**
   * Base-URL voor de AAVA Intake Conversation API. `null` = AAVA-call
   * wordt overgeslagen (composer blijft enabled, geen API-roundtrip).
   * In productie expliciet zetten; in dev wordt 'm vanuit de toolbar-
   * preset gevuld (`/proxy/<env>-aava` → customersbase-host root).
   */
  private readonly aavaApiBaseUrl: string | null;

  /**
   * AbortController voor de in-flight contract-fetch. Net als de identity-
   * variant gecanceld op `hostDisconnected`. Tracked zodat een snelle
   * SESSION_RESET → CHANNEL_PICKED-cyclus geen race veroorzaakt waarin
   * twee parallelle fetches landen.
   */
  private contractsAbort: AbortController | null = null;

  constructor(
    host: ReactiveControllerHost,
    api: ChatApiService,
    options: PollingSchedulerConfig & {
      identity?: {
        url: string | null;
        fetch?: typeof fetchIdentity;
        options?: FetchIdentityOptions;
      };
      defaultChannel?: ChannelId | null;
      customersApiBaseUrl?: string | null;
      aavaApiBaseUrl?: string | null;
      /**
       * Portal-route waar de bezoeker naartoe gaat zodra de AAVA-intake
       * server-side is afgerond. De client bouwt
       * `{repairCompletionUrl}/{scenarioId}/{conversationId}` — exact het
       * PoC-`chat-voltooid`-patroon. `null` = geen completion-redirect
       * (completion-poller draait dan niet).
       */
      repairCompletionUrl?: string | null;
      /**
       * `scenarioId` voor de completion-redirect. Fallback voor wanneer de
       * server geen `externalId` op de conversation teruggeeft. `null` =
       * uitsluitend op de server-`externalId` vertrouwen.
       */
      repairScenarioId?: string | null;
      /**
       * Portal-route naar de reparatieverzoek-pagina. Doel van de "Nee, niet
       * opgelost"-tak van de self-fix-vraag: excuusbericht + redirect hierheen.
       * `null` = niet geconfigureerd → geen redirect (chat blijft open).
       */
      repairRequestUrl?: string | null;
      /** Test-override voor `fetchActiveContracts`. Default: echte impl. */
      fetchContractsImpl?: typeof fetchActiveContracts;
      /** Test-override voor `startAavaConversation`. Default: echte impl. */
      startAavaImpl?: typeof startAavaConversation;
      /** Test-override voor `sendAavaMessage`. Default: echte impl. */
      sendAavaImpl?: typeof sendAavaMessage;
      /** Test-override voor `endAavaConversation`. Default: echte impl. */
      endAavaImpl?: typeof endAavaConversation;
      /** Test-override voor `getAavaConversationStatus`. Default: echte impl. */
      getAavaStatusImpl?: typeof getAavaConversationStatus;
      /** Test-override voor de top-level navigatie. Default: `window.location.assign`. */
      navigate?: (url: string) => void;
      /** Completion-poll-interval (ms). Default 10_000 — spiegelt de PoC. */
      aavaCompletionPollIntervalMs?: number;
      /** Vertraging (ms) tussen "voltooid"-bericht en redirect. Default 2000 — spiegelt de PoC. */
      aavaRedirectDelayMs?: number;
    } = {},
  ) {
    this.host = host;
    this.api = api;
    this.identity = {
      url: options.identity?.url ?? null,
      fetch: options.identity?.fetch ?? fetchIdentity,
      options: options.identity?.options ?? {},
    };
    this.defaultChannel = options.defaultChannel ?? null;
    this.customersApiBaseUrl = options.customersApiBaseUrl ?? null;
    this.aavaApiBaseUrl = options.aavaApiBaseUrl ?? null;
    this.repairCompletionUrl = options.repairCompletionUrl ?? null;
    this.repairScenarioId = options.repairScenarioId ?? null;
    this.repairRequestUrl = options.repairRequestUrl ?? null;
    this.fetchContractsImpl = options.fetchContractsImpl ?? fetchActiveContracts;
    this.startAavaImpl = options.startAavaImpl ?? startAavaConversation;
    this.sendAavaImpl = options.sendAavaImpl ?? sendAavaMessage;
    this.endAavaImpl = options.endAavaImpl ?? endAavaConversation;
    this.getAavaStatusImpl = options.getAavaStatusImpl ?? getAavaConversationStatus;
    this.navigate = options.navigate ?? ((url) => window.location.assign(url));
    this.aavaCompletionPollIntervalMs =
      options.aavaCompletionPollIntervalMs ?? 10_000;
    this.aavaRedirectDelayMs = options.aavaRedirectDelayMs ?? 2000;
    this.scheduler = new PollingScheduler(options);
    host.addController(this);
  }

  private readonly fetchContractsImpl: typeof fetchActiveContracts;
  private readonly startAavaImpl: typeof startAavaConversation;
  private readonly sendAavaImpl: typeof sendAavaMessage;
  private readonly endAavaImpl: typeof endAavaConversation;
  private readonly getAavaStatusImpl: typeof getAavaConversationStatus;

  /**
   * Completion-redirect-config. `repairCompletionUrl` = portal-route-base;
   * `repairScenarioId` = fallback-scenarioId wanneer de server geen
   * `externalId` teruggeeft. Zie de constructor-options voor details.
   */
  private readonly repairCompletionUrl: string | null;
  private readonly repairScenarioId: string | null;
  /** Redirect-doel voor de "Nee, niet opgelost"-tak. Zie constructor-options. */
  private readonly repairRequestUrl: string | null;
  /** Top-level navigatie (injecteerbaar voor tests). */
  private readonly navigate: (url: string) => void;
  private readonly aavaCompletionPollIntervalMs: number;
  private readonly aavaRedirectDelayMs: number;

  /** AbortController voor in-flight AAVA-calls — cancellable op disconnect. */
  private aavaAbort: AbortController | null = null;

  /**
   * Completion-poller-state. `aavaCompletionTimer` is de recursieve
   * setTimeout-poll (PoC-stijl, vaste cadans); `aavaRedirectTimer` is de
   * korte leespauze tussen slotbericht en redirect; `aavaStatusAbort`
   * cancelt een in-flight status-fetch; `aavaCompletionScenarioId` bewaart
   * de server-`externalId` zodat de redirect-builder 'm kan gebruiken.
   */
  private aavaCompletionTimer: ReturnType<typeof setTimeout> | null = null;
  private aavaRedirectTimer: ReturnType<typeof setTimeout> | null = null;
  private aavaStatusAbort: AbortController | null = null;
  private aavaCompletionScenarioId: string | null = null;
  /**
   * Conversation die op dit moment actief gepolld wordt, of `null` als de
   * poller gestopt is. Het reschedule-pad leest dit zodat een stop midden in
   * een tick (reset / not_found / finished) niet alsnog een volgende poll
   * inschiet — een null-timer alleen volstaat niet, want dat is óók de
   * normale "net gevuurd"-staat.
   */
  private aavaPollConversationId: string | null = null;

  hostConnected(): void {
    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    window.addEventListener('storage', this.onStorageEvent);
    this.scheduler.setVisibility(!document.hidden);
    void this.bootstrap();
  }

  hostDisconnected(): void {
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('storage', this.onStorageEvent);
    this.scheduler.stopAll();
    if (this.intakeSubmitTimer) {
      clearTimeout(this.intakeSubmitTimer);
      this.intakeSubmitTimer = null;
    }
    if (this.attachRevealHandle !== null) {
      clearTimeout(this.attachRevealHandle);
      this.attachRevealHandle = null;
    }
    if (this.identityAbort !== null) {
      this.identityAbort.abort();
      this.identityAbort = null;
    }
    if (this.contractsAbort !== null) {
      this.contractsAbort.abort();
      this.contractsAbort = null;
    }
    if (this.aavaAbort !== null) {
      this.aavaAbort.abort();
      this.aavaAbort = null;
    }
    this.stopAavaCompletionPolling();
  }

  /**
   * Cross-tab synchronisatie via de browser's `storage`-event. Twee scenario's:
   *
   *   1. **Andere tab heeft de session gewist** (newValue=null): bezoeker
   *      klikte daar "Nieuw gesprek" of er was SESSION_LOST. Wij resetten
   *      mee zodat we niet blijven hangen op een stale state.
   *
   *   2. **Andere tab heeft de rating geüpdatet** (newValue=JSON): bezoeker
   *      ratte daar of klikte "Niet nu". Wij brengen onze ended-state in
   *      lijn via RATING_HYDRATED — anders zou bezoeker hier een tweede
   *      rating kunnen sturen.
   *
   * Beide paden zijn idempotent: dispatch alleen wanneer state daadwerkelijk
   * verandert (reducer doet die check).
   */
  private readonly onStorageEvent = (e: StorageEvent): void => {
    if (e.key === VISITOR_KEY) {
      this.handleVisitorStorageEvent(e);
      return;
    }
    if (e.key !== SESSION_KEY) return;

    if (e.newValue === null) {
      // Andere tab heeft localStorage gewist — reflecteer in deze tab.
      if (this.state.session.phase !== 'none') {
        this.dispatch({ type: 'SESSION_RESET' });
      }
      return;
    }

    // Niet-clear update: kijk of het rating-veld in de snapshot wijzigde
    // én of wij in een ended-state met rating-slice zitten waar dat van
    // toepassing is. Anders is er niets te synchroniseren.
    if (
      this.state.session.phase !== 'ended' ||
      this.state.session.rating === null
    ) {
      return;
    }

    const parsed = parsePersistedSession(e.newValue);
    const incoming = parsed?.endedSnapshot?.rating ?? null;
    if (incoming === null) return;

    // Reducer detecteert zelf no-op-updates via ratingEqual; we hoeven hier
    // dus niet pre-te-vergelijken.
    this.dispatch({ type: 'RATING_HYDRATED', rating: incoming });
  };

  /**
   * Cross-tab visitor-sync. Andere tab heeft visitor-data gemuteerd:
   *   - newValue=null → bezoeker heeft "Wis mijn gegevens" geklikt; wij
   *     forgetten ook (en de FORGOTTEN-reactor cascadeert SESSION_RESET).
   *   - newValue=JSON → wijziging via settings-menu; wij hydrateren mee
   *     zodat beide tabs dezelfde naam/email tonen.
   */
  private handleVisitorStorageEvent(e: StorageEvent): void {
    if (e.newValue === null) {
      if (this.state.visitor !== null) {
        this.dispatch({ type: 'VISITOR_FORGOTTEN' });
      }
      return;
    }
    const incoming = parsePersistedVisitor(e.newValue);
    if (!incoming) return;
    this.dispatch({
      type: 'VISITOR_HYDRATED',
      firstName: incoming.firstName,
      email: incoming.email,
    });
  }

  // ===========================================================================
  // Visibility — pauzeer alle polling als de tab in de achtergrond zit
  // ===========================================================================

  private readonly onVisibilityChange = (): void => {
    // Scheduler regelt zelf pause/resume + immediate-fetch-on-resume.
    this.scheduler.setVisibility(!document.hidden);
  };

  private async bootstrap(): Promise<void> {
    await this.fetchServiceStatusOnce();

    // Visitor-hydration vóór session-hydration: als de bezoeker een
    // lopende sessie had ÉN visitor-data, willen we eerst visitor in state
    // zodat de skip-logic in INTAKE_QUESTION_RECEIVED correct werkt
    // (al is dat hier niet relevant — sessie is al doorlopen). Volgorde
    // staat vast om verrassingen te voorkomen.
    const persistedVisitor = loadPersistedVisitor();
    if (persistedVisitor) {
      this.dispatch({
        type: 'VISITOR_HYDRATED',
        firstName: persistedVisitor.firstName,
        email: persistedVisitor.email,
      });
    }

    // AAVA-session-restore vóór classic-session: ze zijn mutually
    // exclusive (AAVA = repair-request kanaal, classic = webchat) en
    // AAVA's persistentie is veel goedkoper (alleen 2 IDs i.p.v. een
    // hele snapshot). Bij geslaagde restore skippen we classic-flow.
    const persistedAava = loadAavaSession();
    if (
      persistedAava &&
      this.aavaApiBaseUrl !== null &&
      this.identity.url !== null
    ) {
      const restored = await this.restoreAavaSession(persistedAava);
      if (restored) return;
      // Restore-fout (404, 401, parse) — fall through naar normale flow.
    }

    // Resume: als we een lokaal bewaarde sessie hebben, probeer hem terug te
    // pakken. De polling die start na SESSION_STARTED valideert zelf of de
    // sessie nog leeft (404 → SESSION_LOST → storage wordt automatisch gewist).
    const persisted = loadPersistedSession();
    if (persisted?.endedSnapshot) {
      // Geëindigd gesprek — herstel direct uit lokale snapshot, geen
      // polling. Server kan de sessie inmiddels hebben opgeruimd na /leave.
      this.dispatch({
        type: 'HYDRATE_ENDED',
        serverMessages: persisted.endedSnapshot.serverMessages,
        assistantMessages: persisted.endedSnapshot.assistantMessages,
        operator: persisted.endedSnapshot.operator,
        endedAt: persisted.endedSnapshot.endedAt,
        reason: persisted.endedSnapshot.reason,
        id: persisted.endedSnapshot.sessionId ?? null,
        rating: persisted.endedSnapshot.rating ?? null,
      });
      // Voorkomt dat de unread-badge alle operator-berichten uit de
      // snapshot opnieuw als ongelezen telt na refresh: de bezoeker zag
      // ze al toen het gesprek eindigde, ergo lastReadAt mag niet lager
      // zijn dan de hoogste operator-timestamp in de snapshot.
      const snapshotOpTs = persisted.endedSnapshot.serverMessages.reduce(
        (max, m) =>
          m.author === 'operator' && m.timestamp > max ? m.timestamp : max,
        0,
      );
      this.dispatch({
        type: 'STATE_HYDRATED',
        panelOpen: persisted.panelOpen,
        lastReadAt: Math.max(persisted.lastReadAt, snapshotOpTs),
      });
      // Geen polling — ended-gesprek verandert niet meer. Status-poller
      // gaat aan zodat we openings-status nog wel oppikken.
      this.scheduler.startStatusPolling(() => this.fetchServiceStatusOnce());
      return;
    }
    if (persisted) {
      // HYDRATE_ACTIVE slaat de queued→active-transitie over voor
      // gesprekken die al een operator hadden — voorkomt een tweede
      // searching-banner-flash bij refresh.
      if (persisted.activeSnapshot) {
        this.dispatch({
          type: 'HYDRATE_ACTIVE',
          id: persisted.id,
          mode: persisted.mode,
          operator: persisted.activeSnapshot.operator,
          serverMessages: persisted.activeSnapshot.serverMessages,
        });
      } else {
        // Sessies in queued op moment van save (en pre-rollout-snapshots
        // zonder activeSnapshot) → polling vindt de operator alsnog.
        this.dispatch({
          type: 'SESSION_STARTED',
          id: persisted.id,
          mode: persisted.mode,
        });
      }
      // UI-state hydrateren: panel-open + last-read-marker, plus de bot-
      // Q&A en system-pills uit het assistant-tijdvak (zonder die zou de
      // bezoeker na refresh alleen z'n naakte chat-thread zien — geen
      // greeting, geen "naam?"-Q&A, geen "operator gekoppeld"-pill).
      // STATE_HYDRATED schrijft niet terug naar storage (no-op write
      // voorkomen).
      this.dispatch({
        type: 'STATE_HYDRATED',
        panelOpen: persisted.panelOpen,
        lastReadAt: persisted.lastReadAt,
        assistantMessages: persisted.assistantMessages,
      });
      // Conversation-polling start nu via handleSideEffects — service-status
      // poller niet nodig zolang we in queued/active zitten.
      return;
    }

    // Geen sessie → start 5s service-status-polling zodat we 't merken
    // wanneer de chat online/offline gaat (b.v. openingstijden, agent-login).
    this.scheduler.startStatusPolling(() => this.fetchServiceStatusOnce());

    // Panel-open-voorkeur herstellen, ook zonder sessie. Bezoekers die op
    // marketing-pagina's de chat openen en doornavigeren willen 'm niet
    // elke keer dicht zien.
    const panelPref = loadPanelOpenPref();
    if (panelPref === true) {
      this.dispatch({ type: 'STATE_HYDRATED', panelOpen: true, lastReadAt: 0 });
    }

    // Pre-session state herstellen: kanaal-keuze, bot-flow en conversation
    // bewaard tussen page-navigaties. Wordt overschreven zodra een echte
    // session start (SESSION_STARTED → clearPreSession).
    const preSession = loadPreSession();
    if (preSession) {
      this.dispatch({
        type: 'PRE_SESSION_HYDRATED',
        pickedChannel: preSession.pickedChannel,
        pickerStep: preSession.pickerStep,
        intake: preSession.intake,
        assistant: preSession.assistant,
        assistantMessages: preSession.assistantMessages,
      });
    }

    // Single-channel-config: er is geen picker-UI om CHANNEL_PICKED te
    // triggeren. Dispatchen we 'm zelf zodat de identity-flow zijn
    // natuurlijke trigger heeft (zelfde pad als multi-channel).
    // SKIP wanneer pre-session-hydration al een channel-pick teruggebracht
    // heeft — anders overschrijft auto-pick de herstelde keuze.
    if (!preSession?.pickedChannel) {
      this.autoPickSingleChannel();
    } else {
      this.resumeIdentityCheckAfterHydration();
    }
  }

  /**
   * Na pre-session-hydratie staat `pickedChannel` weer goed, maar `auth` en
   * `identityJwt` zijn bewust niet gepersisteerd (XSS-policy). Zonder een
   * extra trigger zou de identity-check niet meer afgaan — die hangt aan
   * de `CHANNEL_PICKED`-action, en die wordt bij hydratie niet opnieuw
   * gedispatcht (zou bot-state overschrijven).
   *
   * Bezoekers die in `aava-selecting-contract` zaten zouden anders na
   * refresh in een dead-end belanden: contract-balloon blijft hangen in
   * de skeleton/"Laden…"-state want `reactToAavaContractFetch` wacht op
   * een `identityJwt`-transitie die nooit komt.
   *
   * Resolutie zet de bestaande keten weer in gang: identity-check →
   * IDENTITY_RESOLVED_WITH_JWT → `reactToAavaContractFetch` detecteert de
   * transitie en start de contract-fetch.
   */
  private resumeIdentityCheckAfterHydration(): void {
    if (this.identity.url === null) return;
    const state = this.state;
    if (state.auth !== 'unknown') return;
    if (state.session.phase !== 'none') return;
    const channel = state.ui.pickedChannel;
    if (channel !== 'webchat' && channel !== 'repair-request') return;
    this.dispatch({ type: 'IDENTITY_CHECK_STARTED' });
    void this.runIdentityCheck();
  }

  /**
   * Dispatch CHANNEL_PICKED voor het enige kanaal in een single-channel-
   * config. Idempotent: skipt als er al een pick of een lopende sessie is.
   */
  private autoPickSingleChannel(): void {
    if (this.defaultChannel === null) return;
    if (this.state.session.phase !== 'none') return;
    if (this.state.ui.pickedChannel !== null) return;
    this.dispatch({ type: 'CHANNEL_PICKED', channel: this.defaultChannel });
  }

  private async fetchServiceStatusOnce(): Promise<void> {
    try {
      const status = await this.api.fetchServiceStatus();
      this.dispatch({ type: 'SERVICE_STATUS_FETCHED', status });
    } catch {
      this.dispatch({ type: 'SERVICE_STATUS_FAILED' });
    }
  }

  get(): Readonly<ChatState> {
    return this.state;
  }

  dispatch(action: ChatAction): void {
    const prev = this.state;
    const next = chatReducer(prev, action);
    if (next !== prev) {
      this.state = next;
      this.host.requestUpdate();
    }
    this.handleSideEffects(prev, next, action);
  }

  // ===========================================================================
  // Side-effects
  // ===========================================================================

  /**
   * Router voor side-effects na elke dispatch. Pure orchestratie — alle
   * werkelijke logica zit in de benoemde reactors hieronder zodat elke tak
   * los te lezen + te begrijpen is.
   */
  private handleSideEffects(
    prev: ChatState,
    next: ChatState,
    action: ChatAction,
  ): void {
    this.reactToAssistantSearchTransition(prev, next);
    this.reactToPanelOpenTransition(prev, next);
    this.reactToChannelPickIdentityCheck(prev, next, action);
    this.reactToAavaContractFetch(prev, next);
    this.reactToAavaMessageSend(prev, next, action);
    this.reactToAavaCompletionPolling(prev, next);
    this.reactToAavaFinished(prev, next);
    this.reactToAavaSelfFixAsking(prev, next);
    this.reactToSessionRequest(prev, next, action);
    this.reactToPollingSwap(prev, next);
    // Rating-POST staat bewust vóór persistence — de cross-tab dedupe-
    // check leest storage en moet de stand-vóór-deze-dispatch zien (anders
    // ziet 'ie z'n eigen zojuist-geschreven submitted en skipt onterecht).
    this.reactToRatingSubmission(prev, action);
    this.reactToPersistence(prev, next, action);
    this.reactToCustomerEndedConversation(prev, action);
    this.reactToAavaEnd(prev, action);
    this.reactToMessageDispatch(next, action);
    this.reactToSessionLost(action);
    this.reactToVisitorForgotten(prev, action);
    this.reactToResetAutoPick(action);
  }

  /**
   * Bezoeker klikt "Nieuw gesprek" (SESSION_RESET) in een single-channel-
   * config: pickedChannel is door de reducer gewist, maar er is geen
   * picker-UI om opnieuw te kiezen. Dispatchen we 'm dus zelf zodat de
   * identity-flow opnieuw kan vuren — auth gaat sowieso al naar 'unknown'
   * via de reducer, dus dit is precies de signal die `reactToChannelPick`
   * nodig heeft om opnieuw te fetchen.
   */
  private reactToResetAutoPick(action: ChatAction): void {
    if (action.type !== 'SESSION_RESET') return;
    this.autoPickSingleChannel();
  }

  /**
   * Bot-flow → 'searching': na 1.2s automatisch INTAKE_SUBMITTED dispatchen
   * zodat het "Ik zoek nu een medewerker..."-bericht leesbaar blijft voordat
   * de UI naar queue-state schakelt. Timer tracked zodat 'm cancellable is
   * op SESSION_RESET / unmount.
   */
  private reactToAssistantSearchTransition(
    prev: ChatState,
    next: ChatState,
  ): void {
    if (
      prev.assistant?.step === 'searching' ||
      next.assistant?.step !== 'searching' ||
      next.intake === null ||
      next.visitor === null
    ) {
      return;
    }
    const intake = next.intake;
    const visitor = next.visitor;
    if (this.intakeSubmitTimer) clearTimeout(this.intakeSubmitTimer);
    this.intakeSubmitTimer = setTimeout(() => {
      this.intakeSubmitTimer = null;
      this.dispatch({
        type: 'INTAKE_SUBMITTED',
        question: intake.question,
        captchaToken: intake.captchaToken,
        visitor,
      });
    }, 1200);
  }

  /**
   * Panel open/close beïnvloedt poll-intervals (5s vs 30s status, 2s vs
   * 6s conv) en triggert immediate fetch op transitie naar open. Scheduler
   * regelt beide intern.
   */
  private reactToPanelOpenTransition(prev: ChatState, next: ChatState): void {
    if (prev.ui.panelOpen === next.ui.panelOpen) return;
    this.scheduler.setPanelOpen(next.ui.panelOpen);
  }

  /**
   * Channel-pick (`webchat` of `repair-request`) → identity-check tegen
   * de host-portal (B2-strategie: lazy bij channel-pick). Skip wanneer:
   *   - Geen `identity.url` geconfigureerd (host gebruikt de chat puur
   *     anoniem — bijv. een marketing-site zonder login).
   *   - De auth-status is al bekend (`!== 'unknown'`) — bezoeker heeft
   *     bv. eerder `webchat` gepakt en daarna alsnog `repair-request`.
   *     Geen tweede roundtrip nodig binnen dezelfde sessie.
   *   - Channel is geen webchat/repair-request (whatsapp/messenger
   *     hebben niets aan een JWT).
   *   - Bezoeker zit al in een lopende sessie.
   *
   * Resolutie dispatcht vervolgens IDENTITY_RESOLVED_WITH_JWT (auth-pad
   * neemt het over) of IDENTITY_RESOLVED_WITHOUT_JWT (terug naar 'none').
   */
  private reactToChannelPickIdentityCheck(
    prev: ChatState,
    next: ChatState,
    action: ChatAction,
  ): void {
    if (action.type !== 'CHANNEL_PICKED') return;
    if (prev.ui.pickedChannel === next.ui.pickedChannel) return;
    if (this.identity.url === null) return;
    if (next.auth !== 'unknown') return;
    if (next.session.phase !== 'none') return;
    const channel = next.ui.pickedChannel;
    if (channel !== 'webchat' && channel !== 'repair-request') return;

    this.dispatch({ type: 'IDENTITY_CHECK_STARTED' });
    void this.runIdentityCheck();
  }

  /**
   * Publiek entry-point voor de retry-knop in de inline contract-choices.
   * Triggert dezelfde flow als de eerste fetch — alleen bruikbaar in
   * AVAA-state met een geldige JWT en customersApiBaseUrl.
   */
  retryContractFetch(): void {
    const state = this.state;
    if (this.customersApiBaseUrl === null) return;
    if (state.identityJwt === null) return;
    if (state.contracts.status === 'loading') return;
    this.dispatch({ type: 'CONTRACTS_FETCH_STARTED' });
    void this.runContractFetch(this.customersApiBaseUrl, state.identityJwt);
  }

  /**
   * Trigger contract-fetch zodra zowel de AVAA-flow als de JWT klaar zijn.
   *
   * Twee triggering-paden, allebei nodig omdat AVAA en JWT in willekeurige
   * volgorde kunnen aankomen:
   *
   *   1. **CHANNEL_PICKED voor repair-request met identity URL**: AVAA
   *      wordt direct opgestart (`assistant.step = aava-asking`) maar de
   *      identity-check is nog niet klaar. JWT komt later via
   *      IDENTITY_RESOLVED_WITH_JWT.
   *   2. **CHANNEL_PICKED na een eerdere webchat-pick**: auth is al
   *      `'authenticated'` en JWT is al gezet. AVAA komt door deze pick
   *      pas tot stand.
   *
   * We detecteren dus elke transitie waarbij beide voorwaarden nieuw
   * waar zijn (of waar ze gezamenlijk voor het eerst waar worden). Skip-
   * voorwaarden:
   *   - `customersApiBaseUrl` niet gezet (POC-fallback, dev zonder API);
   *   - contracts-slice al loaded/loading (idempotent).
   */
  private reactToAavaContractFetch(prev: ChatState, next: ChatState): void {
    if (this.customersApiBaseUrl === null) return;
    const ready = (s: ChatState): boolean =>
      s.assistant?.step === 'aava-selecting-contract' && s.identityJwt !== null;
    if (!ready(next) || ready(prev)) return;
    if (next.contracts.status === 'loading' || next.contracts.status === 'loaded') {
      return;
    }
    this.dispatch({ type: 'CONTRACTS_FETCH_STARTED' });
    void this.runContractFetch(this.customersApiBaseUrl, next.identityJwt!);
  }

  private async runContractFetch(baseUrl: string, jwt: string): Promise<void> {
    this.contractsAbort?.abort();
    const abort = new AbortController();
    this.contractsAbort = abort;
    const result = await this.fetchContractsImpl(baseUrl, jwt, {
      signal: abort.signal,
    });
    // Hostmount kan tussentijds zijn weggegaan of een nieuwere fetch
    // staat al uit — dan niet meer dispatchen op deze stale promise.
    if (this.contractsAbort !== abort) return;
    this.contractsAbort = null;
    if (result.ok) {
      this.dispatch({
        type: 'CONTRACTS_FETCH_SUCCEEDED',
        items: result.data.items,
      });
    } else {
      this.dispatch({
        type: 'CONTRACTS_FETCH_FAILED',
        reason: result.reason,
      });
    }
  }

  /**
   * Detecteert wanneer een bezoeker een bericht stuurt in de AAVA-flow
   * (`ASSISTANT_USER_REPLIED` in step `'aava-asking'`) en kicked dan de
   * AAVA-call af: `/start` indien `conversationId` nog null is, anders
   * direct `/message` op de bestaande conversation.
   *
   * Skipt wanneer:
   *   - geen `customersApiBaseUrl` (POC zonder AAVA)
   *   - geen `identityJwt` (race / handmatige state-injectie)
   *   - geen `selectedUnitId` (contract niet gekozen — defensief, mag
   *     niet voorkomen omdat de step alleen via CONTRACT_SELECTED komt)
   *   - status === 'starting' / 'sending' (al een call in-flight)
   */
  private reactToAavaMessageSend(
    prev: ChatState,
    next: ChatState,
    action: ChatAction,
  ): void {
    if (action.type !== 'ASSISTANT_USER_REPLIED') return;
    if (next.assistant?.step !== 'aava-asking') return;
    if (this.aavaApiBaseUrl === null) return;
    if (next.identityJwt === null) return;
    // Na een afgeronde conversation geen vervolg-berichten meer sturen — de
    // bezoeker wordt doorgestuurd of krijgt de self-fix-vraag; een late
    // /message is zinloos. `Resolved` is óók terminaal (self-fix-tak).
    if (next.aava.finished || next.aava.outcome === 'Resolved') return;
    if (next.aava.status === 'starting' || next.aava.status === 'sending') {
      return;
    }
    const unitId = selectSelectedUnitId(next);
    if (unitId === null) return;
    const text = action.text.trim();
    // DataUris staan op `prev.stagedFiles` (reducer wist 'm meteen na
    // ASSISTANT_USER_REPLIED). We filteren `undefined`-dataUris weg
    // voor het geval een file om wat voor reden niet correct gelezen is.
    const images = prev.stagedFiles
      .map((f) => f.dataUri)
      .filter((v): v is string => typeof v === 'string' && v.length > 0);
    if (text.length === 0 && images.length === 0) return;

    this.dispatch({ type: 'AAVA_REQUEST_STARTED' });
    void this.runAavaSend(
      this.aavaApiBaseUrl,
      next.identityJwt,
      unitId,
      text,
      images,
      next.aava.conversationId,
    );
  }

  /**
   * Twee-stappen AAVA-flow: `/start` (alleen als nog geen conversation)
   * gevolgd door `/message`. Bij elk fail-pad dispatcht `AAVA_REQUEST_FAILED`
   * zodat de UI de error-state aanneemt; we proberen niet automatisch
   * te retryen — dat laten we aan de bezoeker over (knop verschijnt
   * in een latere iteratie).
   */
  private async runAavaSend(
    baseUrl: string,
    jwt: string,
    unitId: string,
    text: string,
    images: string[],
    existingConversationId: string | null,
  ): Promise<void> {
    this.aavaAbort?.abort();
    const abort = new AbortController();
    this.aavaAbort = abort;

    let conversationId = existingConversationId;
    if (conversationId === null) {
      const startResult = await this.startAavaImpl(
        baseUrl,
        jwt,
        { unitId },
        { signal: abort.signal },
      );
      if (this.aavaAbort !== abort) return;
      if (!startResult.ok) {
        this.aavaAbort = null;
        this.dispatch({ type: 'AAVA_REQUEST_FAILED', reason: startResult.reason });
        return;
      }
      conversationId = startResult.conversationId;
      this.dispatch({ type: 'AAVA_CONVERSATION_CREATED', conversationId });
    }

    const msgResult = await this.sendAavaImpl(
      baseUrl,
      jwt,
      // Lege images-array niet meesturen (postJson laat 'm weg als
      // het veld undefined is) — sommige deployments balken op
      // `images: []` in de body.
      images.length > 0
        ? { conversationId, text, images }
        : { conversationId, text },
      { signal: abort.signal },
    );
    if (this.aavaAbort !== abort) return;
    this.aavaAbort = null;
    if (!msgResult.ok) {
      this.dispatch({ type: 'AAVA_REQUEST_FAILED', reason: msgResult.reason });
      return;
    }
    // AAVA mag een `null`-text leveren (zeldzaam, upstream-shrug). We
    // dispatchen niets in dat geval — composer ontgrendelt door reducer
    // die op REQUEST_FAILED ofwel REPLY_RECEIVED reageert. Treat null as
    // a failed reply zodat de UI consistent reageert.
    if (msgResult.text === null) {
      this.dispatch({ type: 'AAVA_REQUEST_FAILED', reason: 'parse' });
      return;
    }
    this.dispatch({
      type: 'AAVA_REPLY_RECEIVED',
      text: msgResult.text,
      messageId: msgResult.messageId,
    });
  }

  /**
   * Start/stop de completion-poller op basis van de conversation-lifecycle.
   * Spiegelt de PoC-`pollMessages`-flow: zodra er een `conversationId` is,
   * pollt de client periodiek `GET /conversation/{id}` tot `finishedAt`
   * gezet is. Stopt zodra de conversation verdwijnt (SESSION_RESET).
   *
   * Alleen actief wanneer `repairCompletionUrl` geconfigureerd is — zonder
   * redirect-doel heeft pollen geen zin.
   */
  private reactToAavaCompletionPolling(prev: ChatState, next: ChatState): void {
    if (this.repairCompletionUrl === null) return;
    const prevId = prev.aava.conversationId;
    const nextId = next.aava.conversationId;
    if (prevId === null && nextId !== null && !next.aava.finished) {
      this.startAavaCompletionPolling(nextId);
    } else if (prevId !== null && nextId === null) {
      this.stopAavaCompletionPolling();
    }
  }

  /**
   * Reageert op de afgeronde conversation: stop de poller en stuur de
   * bezoeker — na een korte leespauze zodat hij het slotbericht ziet —
   * door naar `{repairCompletionUrl}/{scenarioId}/{conversationId}`,
   * exact het PoC-`chat-voltooid`-patroon.
   *
   * `buildCompletionUrl` draait vóór `stopAavaCompletionPolling` zodat de
   * net-gepollde `scenarioId` nog beschikbaar is.
   */
  private reactToAavaFinished(prev: ChatState, next: ChatState): void {
    if (!next.aava.finished || prev.aava.finished) return;
    const url = this.buildCompletionUrl(next.aava.conversationId);
    this.stopAavaCompletionPolling();
    if (url === null) return;
    this.aavaRedirectTimer = setTimeout(() => {
      this.aavaRedirectTimer = null;
      this.navigate(url);
    }, this.aavaRedirectDelayMs);
  }

  /**
   * Een `Resolved`-conversation stelt de self-fix-vraag i.p.v. te redirecten.
   * Stop de completion-poller zodra de vraag verschijnt — verder pollen heeft
   * geen zin, de bezoeker beslist nu met ja/nee.
   */
  private reactToAavaSelfFixAsking(prev: ChatState, next: ChatState): void {
    if (next.aava.selfFix === 'asking' && prev.aava.selfFix !== 'asking') {
      this.stopAavaCompletionPolling();
    }
  }

  /**
   * "Ja, opgelost" — de backend legde `Resolved` al vast, dus client-only:
   * paneel sluiten + sessie resetten zodat een volgende opening schoon start.
   */
  onSelfFixConfirm(): void {
    this.dispatch({ type: 'AAVA_SELF_FIX_CONFIRMED' });
    this.dispatch({ type: 'PANEL_CLOSED' });
    this.dispatch({ type: 'SESSION_RESET' });
  }

  /**
   * "Nee, niet opgelost" — toon het excuusbericht (reducer) en stuur de
   * bezoeker na een korte leespauze door naar de reparatieverzoek-pagina.
   * Zonder geconfigureerde `repairRequestUrl` blijft de chat open (geen kapotte
   * navigatie) — spiegelt de null-guard van `buildCompletionUrl`.
   */
  onSelfFixDecline(): void {
    this.dispatch({ type: 'AAVA_SELF_FIX_DECLINED' });
    const url = this.repairRequestUrl;
    if (url === null || url.length === 0) {
      console.warn('[chat] repairRequestUrl niet geconfigureerd — geen redirect na "niet opgelost".');
      return;
    }
    this.aavaRedirectTimer = setTimeout(() => {
      this.aavaRedirectTimer = null;
      this.navigate(url);
    }, this.aavaRedirectDelayMs);
  }

  private startAavaCompletionPolling(conversationId: string): void {
    if (this.repairCompletionUrl === null) return;
    this.stopAavaCompletionPolling();
    this.aavaCompletionScenarioId = null;
    this.aavaPollConversationId = conversationId;
    this.scheduleNextAavaPoll(conversationId);
  }

  private stopAavaCompletionPolling(): void {
    this.aavaPollConversationId = null;
    if (this.aavaCompletionTimer !== null) {
      clearTimeout(this.aavaCompletionTimer);
      this.aavaCompletionTimer = null;
    }
    if (this.aavaRedirectTimer !== null) {
      clearTimeout(this.aavaRedirectTimer);
      this.aavaRedirectTimer = null;
    }
    if (this.aavaStatusAbort !== null) {
      this.aavaStatusAbort.abort();
      this.aavaStatusAbort = null;
    }
  }

  private scheduleNextAavaPoll(conversationId: string): void {
    this.aavaCompletionTimer = setTimeout(() => {
      this.aavaCompletionTimer = null;
      void this.runAavaCompletionPoll(conversationId).then(() => {
        // Alleen doorpollen als deze conversation nog actief gepolld wordt —
        // de poll-tick zelf kan 'm hebben gestopt (not_found / finished) of
        // een SESSION_RESET kan tussendoor zijn gebeurd.
        if (this.aavaPollConversationId === conversationId) {
          this.scheduleNextAavaPoll(conversationId);
        }
      });
    }, this.aavaCompletionPollIntervalMs);
  }

  /**
   * Eén poll-tick: leest de conversation-status via de `/aava`-proxy. Faalt
   * stil (next tick probeert opnieuw), behalve 404 (`not_found`) — dan is de
   * conversation server-side weg en stoppen we i.p.v. eindeloos kloppen.
   * Op `finished` bewaart-ie de server-`scenarioId` en dispatcht
   * AAVA_CONVERSATION_FINISHED; de redirect volgt via `reactToAavaFinished`.
   */
  private async runAavaCompletionPoll(conversationId: string): Promise<void> {
    const jwt = this.state.identityJwt;
    // Geen JWT (identity-check nog niet rond) → niet pollen; volgende tick.
    if (this.aavaApiBaseUrl === null || jwt === null) return;
    this.aavaStatusAbort?.abort();
    const abort = new AbortController();
    this.aavaStatusAbort = abort;
    const result = await this.getAavaStatusImpl(
      this.aavaApiBaseUrl,
      jwt,
      conversationId,
      { signal: abort.signal },
    );
    if (this.aavaStatusAbort !== abort) return; // stale (gestopt/herstart)
    this.aavaStatusAbort = null;
    if (!result.ok) {
      if (result.reason === 'not_found') this.stopAavaCompletionPolling();
      return;
    }
    // Vangnet: reconcile gemiste assistant-replies vóór de finished-check,
    // zodat een gemiste laatste reply nog zichtbaar wordt ook al rondt
    // dezelfde tick de conversation af. De reducer dedupliceert op messageId.
    this.reconcileAavaMessages(result.status.messages);
    if (result.status.finished) {
      this.aavaCompletionScenarioId = result.status.scenarioId;
      this.dispatch({
        type: 'AAVA_CONVERSATION_FINISHED',
        outcome: result.status.outcome,
      });
    }
  }

  /**
   * Zet de gepollde conversation-`messages` om naar reconcile-input: alléén
   * assistant-replies (`sender === 'aidenn'`) met tekst — bezoeker-berichten
   * staan altijd al lokaal (direct geëchood bij verzenden), dus die laten we
   * met rust. De reducer voegt enkel de nog-niet-getoonde toe (dedup op
   * `messageId`), dus dit is idempotent over poll-ticks heen.
   */
  private reconcileAavaMessages(messages: AavaPolledMessage[]): void {
    // Defensief: een server (of test-stub) mag de messages-array weglaten.
    if (!Array.isArray(messages) || messages.length === 0) return;
    const fresh = messages
      .filter((m) => m.sender === 'aidenn' && typeof m.text === 'string' && m.text.length > 0)
      .map((m) => ({
        messageId: m.messageId,
        text: m.text as string,
        timestamp: m.createdAt ? Date.parse(m.createdAt) || Date.now() : Date.now(),
      }));
    if (fresh.length === 0) return;
    this.dispatch({ type: 'AAVA_MESSAGES_RECONCILED', messages: fresh });
  }

  /**
   * Bouwt de portal-`chat-voltooid`-route. `scenarioId` komt bij voorkeur
   * van de server (`externalId` op de conversation) en valt anders terug op
   * de geconfigureerde `repairScenarioId`. Zonder beide → `null` (geen
   * redirect; gelogd) zodat we de bezoeker nooit naar een kapotte URL sturen.
   */
  private buildCompletionUrl(conversationId: string | null): string | null {
    if (this.repairCompletionUrl === null || conversationId === null) {
      return null;
    }
    const scenarioId = this.aavaCompletionScenarioId ?? this.repairScenarioId;
    if (scenarioId === null) {
      console.warn(
        '[chat] AAVA-conversation afgerond maar geen scenarioId beschikbaar ' +
          '(geen externalId van server én geen repairScenarioId-config) — ' +
          'redirect overgeslagen.',
      );
      return null;
    }
    return `${stripTrailingSlash(this.repairCompletionUrl)}/${encodeURIComponent(
      scenarioId,
    )}/${encodeURIComponent(conversationId)}`;
  }

  /**
   * Page-refresh recovery: hydrateer naar 'aava-asking' met de bewaarde
   * IDs, doe een identity-check om de JWT te krijgen, en haal vervolgens
   * de transcript op via `GET /aava-repairs/api/v1/conversation/{id}/messages`.
   *
   * Returns `true` zodra de restore succesvol is afgerond, `false` bij
   * elke fout (storage wordt dan gewist zodat de volgende boot fresh
   * begint). Caller skipt classic-session-restore bij `true`.
   */
  private async restoreAavaSession(
    persisted: PersistedAavaSession,
  ): Promise<boolean> {
    if (this.aavaApiBaseUrl === null) return false;

    // 1. Dispatch hydratie — pickedChannel + assistant.step + contract
    //    + conversationId allemaal terug. UI laat direct 'aava-asking'
    //    zien met loading-indicator tot we de messages hebben.
    this.dispatch({
      type: 'AAVA_SESSION_HYDRATED',
      conversationId: persisted.conversationId,
      contractId: persisted.contractId,
    });

    // 2. Restore alle bewaarde messages (incl. lokaal-only welkom-
    //    prompt en contract-keuze). Geen server-roundtrip nodig —
    //    storage is bron-van-waarheid voor deze tab. `getAavaImpl`
    //    blijft beschikbaar voor toekomstige cross-device-sync of
    //    recovery wanneer storage corrupt blijkt.
    this.dispatch({
      type: 'AAVA_MESSAGES_HYDRATED',
      messages: persisted.messages.map((m) => ({
        id: m.id,
        author: m.author,
        text: m.text,
        timestamp: m.timestamp,
        ...(m.aavaMessageId !== undefined
          ? { aavaMessageId: m.aavaMessageId }
          : {}),
        ...(m.files ? { files: m.files } : {}),
      })),
    });

    // 3. Identity-check op de achtergrond om de JWT terug te krijgen,
    //    zodat vervolgberichten direct kunnen. Bezoeker ziet de chat
    //    al — de identity-check loopt parallel. Zonder cookies →
    //    IDENTITY_RESOLVED_WITHOUT_JWT → fall back naar reset.
    await this.runIdentityCheck();
    const jwt = this.state.identityJwt;
    if (!jwt) {
      clearAavaSession();
      this.dispatch({ type: 'SESSION_RESET' });
      return false;
    }

    // 4. Trigger contract-fetch zodat `state.contracts.items` ook
    //    gevuld is (selectedId is al hersteld). Niet kritisch voor de
    //    chat-flow zelf maar handig voor selectors/getters die contract-
    //    details opvragen (zoals `selectedContract.unit`).
    if (this.customersApiBaseUrl !== null) {
      this.dispatch({ type: 'CONTRACTS_FETCH_STARTED' });
      void this.runContractFetch(this.customersApiBaseUrl, jwt);
    }

    return true;
  }

  private async runIdentityCheck(): Promise<void> {
    if (this.identity.url === null) return;
    // Cancel een eventueel nog lopende eerdere call. In de praktijk
    // beschermt de reducer (`phase === 'identifying'`-guard) al tegen
    // dubbele resolve-dispatches, maar twee parallelle network-requests
    // is gewoon verspilling.
    this.identityAbort?.abort();
    const abort = new AbortController();
    this.identityAbort = abort;
    const result = await this.identity.fetch(this.identity.url, {
      ...this.identity.options,
      signal: abort.signal,
    });
    // Hostmount kan tussentijds zijn weggegaan (SPA-route-change,
    // panel verwijderd). Niet meer dispatchen — reducer + scheduler
    // zijn al opgeruimd in `hostDisconnected`.
    if (this.identityAbort !== abort) return;
    this.identityAbort = null;
    if (result.jwt) {
      this.dispatch({ type: 'IDENTITY_RESOLVED_WITH_JWT', jwt: result.jwt });
    } else {
      this.dispatch({ type: 'IDENTITY_RESOLVED_WITHOUT_JWT' });
    }
  }

  /**
   * Phase 'requesting' → POST naar /anonymous/new of /authenticated/new.
   *
   *   - mode === 'anonymous': vereist `visitor` (naam + email) en de
   *     captcha-token uit INTAKE_SUBMITTED.
   *   - mode === 'authenticated': vereist alleen `jwt` (zit op de phase
   *     zelf, IDENTITY_RESOLVED_WITH_JWT plaatste 'm daar). Geen
   *     captcha — server identificeert via Bearer-header.
   *
   * Voor authenticated sessions is er geen intake-form geweest — de
   * `initialQuestion` is leeg. Dat is hetzelfde gedrag als de oude chat:
   * bezoeker komt direct in queued/active terecht en typt zijn vraag in
   * de composer.
   */
  private reactToSessionRequest(
    prev: ChatState,
    next: ChatState,
    action: ChatAction,
  ): void {
    if (
      prev.session.phase === 'requesting' ||
      next.session.phase !== 'requesting'
    ) {
      return;
    }
    if (next.session.mode === 'authenticated') {
      void this.startAuthenticatedSession(next.session.jwt);
      return;
    }
    if (next.visitor === null) return;
    const initialQuestion =
      action.type === 'INTAKE_SUBMITTED'
        ? action.question
        : this.findInitialQuestion(next);
    const captchaToken =
      action.type === 'INTAKE_SUBMITTED' ? action.captchaToken : '';
    void this.startAnonymousSession(next.visitor, initialQuestion, captchaToken);
  }

  /**
   * Conversation- en service-status-polling zijn mutually exclusive.
   * Tijdens queued/active draait alleen conv-polling (status-info zit
   * impliciet in de session-response). Bij geen sessie draait alleen
   * status-polling zodat we online/offline-transities oppikken.
   */
  private reactToPollingSwap(prev: ChatState, next: ChatState): void {
    const prevNeedsPolling =
      prev.session.phase === 'queued' || prev.session.phase === 'active';
    const nextNeedsPolling =
      next.session.phase === 'queued' || next.session.phase === 'active';
    if (!prevNeedsPolling && nextNeedsPolling) {
      const sessionId =
        next.session.phase === 'queued' || next.session.phase === 'active'
          ? next.session.id
          : null;
      if (sessionId) {
        this.scheduler.stopStatusPolling();
        this.scheduler.startConvPolling(() => this.pollOnce(sessionId));
      }
    }
    if (prevNeedsPolling && !nextNeedsPolling) {
      this.scheduler.stopConvPolling();
      this.scheduler.startStatusPolling(() => this.fetchServiceStatusOnce());
    }
  }

  /**
   * Persistence-laag in localStorage: snapshot bij SESSION_STARTED, sync
   * bij UI-state-wijzigingen, snapshot van ended-state voor refresh-
   * resilience, en gericht wissen bij SESSION_LOST/RESET en bot-flow-start.
   *
   * SESSION_ENDED wist NIET — anders verliest de bezoeker bij een refresh
   * het ended-gesprek inclusief transcript-download.
   */
  private reactToPersistence(
    prev: ChatState,
    next: ChatState,
    action: ChatAction,
  ): void {
    if (action.type === 'SESSION_STARTED') {
      // SESSION_STARTED fired óók bij hydration: controller dispatch't 'm
      // na loadPersistedSession voor queued sessies. Op dat moment is
      // `next.*` nog op initialState (STATE_HYDRATED met de bewaarde data
      // komt PAS NA SESSION_STARTED). Zonder bescherming zouden we de
      // snapshot overschrijven met empty messages + panelOpen=false +
      // lastReadAt=0 — en bij de eerstvolgende refresh leest de gebruiker
      // dat terug. Detect-hydration via `next.assistantMessages.length`:
      // een echte session-start vanuit INTAKE_QUESTION_RECEIVED heeft
      // altijd ≥1 customer-bericht in de lijst.
      const isHydrationRedispatch = next.assistantMessages.length === 0;
      const existing = isHydrationRedispatch ? loadPersistedSession() : null;
      savePersistedSession(
        action.id,
        action.mode,
        existing?.panelOpen ?? next.ui.panelOpen,
        existing?.lastReadAt ?? next.ui.lastReadAt,
        null,
        existing?.assistantMessages ?? next.assistantMessages,
      );
      // Pre-session snapshot wist alleen op een echte session-start;
      // bij hydration was 'm al gewist toen de session voor het eerst
      // begon.
      if (!isHydrationRedispatch) {
        clearPreSession();
      }
    }
    if (action.type === 'SESSION_RESET') {
      clearPreSession();
    }
    // Assistant-pills mutaties tijdens lopende sessie persisteren.
    // SESSION_AGENT_ATTACHED voegt bv. een "operator gekoppeld"-pill
    // toe; SESSION_ENDED kan een server-systemMessage toevoegen.
    // Filter dispatches die hier al gehandeld worden of waar persist
    // expliciet ongewenst is (HYDRATE_ENDED, STATE_HYDRATED).
    if (
      action.type !== 'STATE_HYDRATED' &&
      action.type !== 'HYDRATE_ENDED' &&
      action.type !== 'HYDRATE_ACTIVE' &&
      action.type !== 'SESSION_STARTED' &&
      action.type !== 'SESSION_RESET' &&
      action.type !== 'SESSION_LOST' &&
      prev.assistantMessages !== next.assistantMessages &&
      (next.session.phase === 'queued' || next.session.phase === 'active')
    ) {
      updatePersistedAssistantMessages(next.assistantMessages);
    }
    if (action.type === 'PANEL_OPENED' || action.type === 'PANEL_CLOSED') {
      updatePersistedPanelOpen(next.ui.panelOpen);
    }

    // Pre-session snapshot schrijven wanneer er nog géén session is.
    // Triggert op channel-pick, bot-flow-transities, conversation-updates
    // en intake-state-wijzigingen. Skipt hydration-actions zodat we geen
    // no-op write doen direct na een read.
    const preSessionRelevant =
      prev.ui.pickedChannel !== next.ui.pickedChannel ||
      prev.ui.pickerStep !== next.ui.pickerStep ||
      prev.assistant !== next.assistant ||
      prev.assistantMessages !== next.assistantMessages ||
      prev.intake !== next.intake;
    if (
      preSessionRelevant &&
      next.session.phase === 'none' &&
      action.type !== 'PRE_SESSION_HYDRATED' &&
      action.type !== 'STATE_HYDRATED' &&
      action.type !== 'HYDRATE_ACTIVE' &&
      action.type !== 'HYDRATE_ENDED' &&
      action.type !== 'SESSION_RESET' &&
      action.type !== 'SESSION_STARTED'
    ) {
      // Lege snapshot (geen channel, geen assistant, geen messages) niet
      // bewaren — daar valt niets aan te herstellen.
      const hasContent =
        next.ui.pickedChannel !== null ||
        next.assistant !== null ||
        next.intake !== null ||
        next.assistantMessages.length > 0;
      if (hasContent) {
        savePreSession({
          pickedChannel: next.ui.pickedChannel,
          pickerStep: next.ui.pickerStep,
          intake: next.intake,
          assistant: next.assistant,
          assistantMessages: next.assistantMessages,
        });
      }
    }

    // STATE_HYDRATED uitsluiten — dat is de hydratation zelf, no-op write
    // voorkomen.
    if (
      action.type !== 'STATE_HYDRATED' &&
      prev.ui.lastReadAt !== next.ui.lastReadAt
    ) {
      updatePersistedLastReadAt(next.ui.lastReadAt);
    }
    if (action.type === 'SESSION_LOST' || action.type === 'SESSION_RESET') {
      clearPersistedSession();
      clearAavaSession();
      clearDraft();
      if (this.intakeSubmitTimer) {
        clearTimeout(this.intakeSubmitTimer);
        this.intakeSubmitTimer = null;
      }
      if (this.attachRevealHandle !== null) {
        clearTimeout(this.attachRevealHandle);
        this.attachRevealHandle = null;
      }
    }
    // AAVA-intake server-side afgerond → de bezoeker wordt doorgestuurd naar
    // de chat-voltooid-pagina. Wis de bewaarde conversation, anders hydrateert
    // de (globaal gemonte) widget op de doelpagina 'm opnieuw, start de
    // completion-poller weer, ziet `finishedAt` nóg een keer en redirect in een
    // lus. Een volgende chat maakt een verse conversation aan die gewoon weer
    // persisteert + pollt.
    if (action.type === 'AAVA_CONVERSATION_FINISHED') {
      clearAavaSession();
    }
    // AAVA-session persisteren: zodra `conversationId` bekend is +
    // bij elke wijziging van `assistantMessages` tijdens de AVAA-flow.
    // We bewaren ALLE messages (incl. lokaal-only welkom-prompt en
    // contract-keuze) zodat een refresh ze 1-op-1 kan herstellen
    // zonder dedupe-puzzel met server-transcript.
    const inAava = next.assistant?.step === 'aava-asking';
    const conversationId = next.aava.conversationId;
    // `!finished` guard: AAVA_CONVERSATION_FINISHED voegt het slotbericht toe
    // (messagesChanged === true) terwijl we nog in 'aava-asking' staan — zonder
    // deze guard zou dat de zojuist-gewiste conversation meteen weer wegschrijven.
    if (
      inAava &&
      conversationId !== null &&
      next.contracts.selectedId &&
      !next.aava.finished
    ) {
      const messagesChanged = prev.assistantMessages !== next.assistantMessages;
      const conversationJustCreated =
        action.type === 'AAVA_CONVERSATION_CREATED';
      if (messagesChanged || conversationJustCreated) {
        saveAavaSession({
          conversationId,
          contractId: next.contracts.selectedId,
          messages: next.assistantMessages.map(toPersistedMessage),
        });
      }
    }
    // Schrijf de active-snapshot bij entry in active én bij elke nieuwe
    // poll-tick die serverMessages muteert. HYDRATE_ACTIVE zelf NIET —
    // we hebben er net van gelezen.
    if (
      action.type !== 'HYDRATE_ACTIVE' &&
      next.session.phase === 'active' &&
      (prev.session.phase !== 'active' ||
        prev.serverMessages !== next.serverMessages)
    ) {
      updatePersistedActiveSnapshot({
        operator: next.session.operator,
        serverMessages: next.serverMessages,
      });
    }

    // Snapshot van ended-state bijwerken in twee gevallen:
    //   1. Net binnengekomen in ended-state (refresh-resilience)
    //   2. Rating-mutatie terwijl al in ended (cross-tab sync via storage)
    // RATING_HYDRATED is de cross-tab sync-action zelf — die mag NIET
    // terugschrijven want dat was net wat de andere tab al deed (loop).
    // Idempotente reducer-no-ops worden gefilterd door `prev !== next`.
    if (next.session.phase === 'ended' && prev.session !== next.session) {
      const enteredEnded =
        prev.session.phase !== 'ended' && action.type !== 'HYDRATE_ENDED';
      const ratingMutation =
        next.session.rating !== null &&
        (action.type === 'RATING_SUBMITTED' ||
          action.type === 'RATING_DISMISSED');
      if (enteredEnded || ratingMutation) {
        updatePersistedEndedSnapshot({
          serverMessages: next.serverMessages,
          assistantMessages: next.assistantMessages,
          operator: next.session.operator,
          endedAt: next.session.endedAt,
          reason: next.session.reason,
          sessionId: next.session.id,
          rating: next.session.rating,
        });
      }
    }
    if (action.type === 'INTAKE_QUESTION_RECEIVED') {
      clearDraft();
    }

    // Visitor-persistentie: INTAKE_SUBMITTED is het eerste moment waarop
    // we zeker weten dat firstName + email gevalideerd zijn. VISITOR_UPDATED
    // komt vanuit het settings-menu — al gevalideerd in de UI.
    if (action.type === 'INTAKE_SUBMITTED' && action.visitor) {
      savePersistedVisitor({
        firstName: action.visitor.firstName,
        email: action.visitor.email,
      });
    }
    if (action.type === 'VISITOR_FORGOTTEN') {
      clearPersistedVisitor();
    }
  }

  /**
   * Bezoeker klikte "Beëindig gesprek" → server-side /leave aanroepen
   * (best-effort, beacon-first) zodat operator de afhaak direct ziet i.p.v.
   * via heartbeat-timeout. reason='server' suppressen — dan is de server
   * zelf al de bron.
   */
  private reactToCustomerEndedConversation(
    prev: ChatState,
    action: ChatAction,
  ): void {
    if (action.type !== 'SESSION_ENDED' || action.reason !== 'customer') return;
    const sessionId =
      prev.session.phase === 'queued' || prev.session.phase === 'active'
        ? prev.session.id
        : null;
    if (sessionId) void this.api.endConversation(sessionId).catch(() => {});
  }

  /**
   * AAVA-conversatie netjes server-side afsluiten wanneer de bezoeker
   * "Chatgesprek sluiten" klikt. Voor AVAA-flow dispatcht
   * `handleEndConversation` een `SESSION_RESET` (er is geen classic
   * `session.phase` om te enden). We pikken die action op en vuren
   * vlak vóór de cleanup een best-effort `POST /end` af zodat
   * `Conversation.FinishedAt` server-side gezet wordt.
   *
   * Best-effort: faalt de call (network, 401) dan blijft de conversation
   * server-side "open" — niet kritisch want client-state is al gewist
   * en de bezoeker kan toch niet meer chatten zonder de
   * conversationId.
   */
  private reactToAavaEnd(prev: ChatState, action: ChatAction): void {
    if (action.type !== 'SESSION_RESET') return;
    if (this.aavaApiBaseUrl === null) return;
    const conversationId = prev.aava.conversationId;
    const jwt = prev.identityJwt;
    if (conversationId === null || jwt === null) return;
    void this.endAavaImpl(this.aavaApiBaseUrl, jwt, conversationId);
  }

  /**
   * MESSAGE_QUEUED / MESSAGE_RETRY_REQUESTED triggeren een POST naar de
   * server. Bij retry pakken we de message uit next-state (status was net
   * teruggeslagen naar 'sending').
   *
   * SESSION_STARTED flusht eventuele pre-session messages: bezoeker mocht
   * tijdens 'searching' / 'requesting' al typen — die berichten zitten in
   * pendingMessages met status='sending' maar `postMessage` skipte de POST
   * (geen session-id). Nu de sessie staat, retry'en we ze in volgorde.
   */
  private reactToMessageDispatch(next: ChatState, action: ChatAction): void {
    if (action.type === 'MESSAGE_QUEUED') {
      void this.postMessage(action.message);
    }
    if (action.type === 'MESSAGE_RETRY_REQUESTED') {
      const message = next.pendingMessages.find(
        (m) => m.localId === action.localId,
      );
      if (message) void this.postMessage(message);
    }
    if (action.type === 'SESSION_STARTED') {
      for (const message of next.pendingMessages) {
        if (message.status === 'sending') {
          void this.postMessage(message);
        }
      }
    }
  }

  /**
   * SESSION_LOST → automatisch SESSION_RESET zodat de bezoeker een
   * fresh intake ziet i.p.v. blijven hangen in een 'error'-state.
   */
  private reactToSessionLost(action: ChatAction): void {
    if (action.type === 'SESSION_LOST') {
      this.dispatch({ type: 'SESSION_RESET' });
    }
  }

  /**
   * VISITOR_FORGOTTEN cascade: als er een lopende sessie is wanneer de
   * bezoeker zijn gegevens wist, zou de server-side conversation-record
   * nog naar de oude email-context wijzen. AVG-onhandig — we resetten
   * de sessie ook (best-effort /leave gefired in customer-ended-reactor
   * als phase nog active was).
   */
  private reactToVisitorForgotten(prev: ChatState, action: ChatAction): void {
    if (action.type !== 'VISITOR_FORGOTTEN') return;
    if (prev.session.phase === 'none') return;
    this.dispatch({ type: 'SESSION_RESET' });
  }

  /**
   * Bezoeker klikte "Verstuur" op de rating-card → POST /rate.
   * Best-effort: de UI toont al de success-state (optimistisch), dus
   * een falende call mag de UI niet blokkeren — we verliezen stil de
   * rating maar de bezoeker ziet "Bedankt voor je feedback".
   *
   * RATING_HYDRATED komt vanuit cross-tab sync en mag NIET een tweede
   * POST triggeren (de andere tab heeft dat al gedaan). Dat onderscheid
   * gebeurt hier door alleen op RATING_SUBMITTED te reageren.
   *
   * Cross-tab dedupe: simultane RATING_SUBMITTED in twee tabs zou twee
   * POSTs veroorzaken (storage-events vuren alleen in *andere* tabs).
   * Vlak voor de POST herlezen we storage; als er al een submitted-
   * rating in staat, is een andere tab ons net voorgegaan en skippen
   * we onze eigen call. Verkleint de race-window van microseconde-tick
   * naar werkelijke netwerk-latency.
   */
  private reactToRatingSubmission(
    prev: ChatState,
    action: ChatAction,
  ): void {
    if (action.type !== 'RATING_SUBMITTED') return;
    if (prev.session.phase !== 'ended') return;
    if (prev.session.rating?.status !== 'idle') return;
    const id = prev.session.id;
    if (!id) return;

    const stored = loadPersistedSession();
    if (stored?.endedSnapshot?.rating?.status === 'submitted') return;

    void this.api.rateConversation(id, action.rating).catch(() => {});
  }

  private findInitialQuestion(state: ChatState): string {
    // De intake-question staat als eerste pending-message; pak hem als initialQuestion.
    const first = state.pendingMessages[0];
    return first?.text ?? '';
  }

  // ===========================================================================
  // API-call wrappers
  // ===========================================================================

  /**
   * Gedeeld foutpad voor `start*Session`-helpers: classify + dispatch
   * SESSION_REQUEST_FAILED. Recoverable mapping: alles behalve een
   * service-unavailable-fout (5xx) is opnieuw te proberen door de
   * bezoeker.
   */
  private dispatchSessionFailure(error: unknown): void {
    const reason = this.classifyError(error);
    this.dispatch({
      type: 'SESSION_REQUEST_FAILED',
      reason,
      recoverable: reason !== 'service_unavailable',
    });
  }

  private async startAuthenticatedSession(jwt: string): Promise<void> {
    try {
      // Lege fallback dekt een race waarin phase op 'requesting' komt
      // zonder voorafgaande INTAKE_QUESTION_RECEIVED (externe re-dispatch).
      const sessionId = await this.api.startAuthenticated({
        jwt,
        initialQuestion: this.state.intake?.question ?? '',
      });
      this.dispatch({
        type: 'SESSION_STARTED',
        id: sessionId,
        mode: 'authenticated',
      });
    } catch (error) {
      this.dispatchSessionFailure(error);
    }
  }

  private async startAnonymousSession(
    visitor: Visitor,
    initialQuestion: string,
    captchaToken: string,
  ): Promise<void> {
    try {
      // De live staging-server geeft 500 op lege firstName/lastName/email
      // (server-side bug — zou een nette 400-validation-error moeten zijn).
      // De assistent vraagt alleen om voornaam (UX-keuze), dus we vullen
      // lastName met een placeholder. Te heroverwegen wanneer de server een
      // nette anonieme flow ondersteunt zonder verplichte naam-velden.
      const sessionId = await this.api.startAnonymous({
        firstName: visitor.firstName || 'Anoniem',
        lastName: visitor.lastName || '-',
        emailAddress: visitor.email || 'noreply@embrace.nl',
        initialQuestion,
        captchaToken,
      });
      this.dispatch({ type: 'SESSION_STARTED', id: sessionId, mode: 'anonymous' });
    } catch (error) {
      this.dispatchSessionFailure(error);
    }
  }

  private async postMessage(message: PendingMessage): Promise<void> {
    if (this.state.session.phase !== 'active' && this.state.session.phase !== 'queued') return;
    const sessionId = this.state.session.id;

    try {
      await this.api.sendMessage(sessionId, message.text);
      this.dispatch({ type: 'MESSAGE_POST_SUCCEEDED', localId: message.localId });
      void this.pollOnce(sessionId);
    } catch {
      this.dispatch({ type: 'MESSAGE_POST_FAILED', localId: message.localId });
    }
  }

  // ===========================================================================
  // Polling-fetch
  // ===========================================================================

  private async pollOnce(sessionId: string): Promise<void> {
    try {
      const conv = await this.api.getState(sessionId);

      // Activity-detectie: vergelijk vóór dispatch zodat we transitions zien.
      const prevMsgCount = this.state.serverMessages.length;
      const prevTyping = this.state.agentTyping;
      const prevQueuePos =
        this.state.session.phase === 'queued'
          ? this.state.session.queueInfo?.position ?? null
          : null;
      const prevOperatorAttached =
        this.state.session.phase === 'active' &&
        this.state.session.operator !== null;

      this.dispatch({ type: 'SERVER_STATE_RECEIVED', messages: conv.messages });

      // Auto-transitie: queued → active zodra operator attached is. Gefaseerd
      // zodat de bezoeker eerst de bot-aankondiging leest voor de UI omslaat:
      //
      //   1) ASSISTANT_OPERATOR_FOUND → bot zegt "Sanne is beschikbaar!"
      //      (typing-indicator 800ms + lees-tijd) — phase blijft 'queued',
      //      searching-banner blijft staan, geen scroll-shift.
      //   2) Na ATTACH_REVEAL_DELAY_MS → SESSION_AGENT_ATTACHED: phase=active,
      //      searching-banner verdwijnt, composer komt op dezelfde plek.
      //
      // De `at`-timestamp van ATTACHED moet vóór het oudste operator-bericht
      // liggen zodat message-list (chronologisch sort) de pill boven de
      // reactie van de medewerker plaatst — relevant na een gepauzeerde
      // polling-resume waarbij operator én bericht in één tick binnenkomen.
      if (
        conv.operator &&
        this.state.session.phase === 'queued' &&
        this.attachRevealHandle === null
      ) {
        const operator = conv.operator;
        const oldestOperatorTs = conv.messages.reduce<number | null>(
          (oldest, m) =>
            m.author === 'operator' &&
            (oldest === null || m.timestamp < oldest)
              ? m.timestamp
              : oldest,
          null,
        );
        // Timestamp-truc: aankondiging vlak vóór de pill, pill vlak vóór
        // het oudste operator-bericht. Chronologische sort plaatst ze
        // dan in deze leesvolgorde:
        //   1) bot-aankondiging "Sanne is beschikbaar!"
        //   2) system-pill      "Sanne heeft je vraag opgepakt"
        //   3) operator-message "Hallo, ik ben Sanne …"
        const pillAt =
          oldestOperatorTs !== null ? oldestOperatorTs - 1 : Date.now();
        const announceAt =
          oldestOperatorTs !== null ? oldestOperatorTs - 2 : Date.now();
        this.dispatch({
          type: 'ASSISTANT_OPERATOR_FOUND',
          operator,
          at: announceAt,
        });
        this.attachRevealHandle = window.setTimeout(() => {
          this.attachRevealHandle = null;
          this.dispatch({
            type: 'SESSION_AGENT_ATTACHED',
            operator,
            at: pillAt,
          });
        }, ChatStateController.ATTACH_REVEAL_DELAY_MS);
      }

      // Queue-info sync
      if (this.state.session.phase === 'queued') {
        this.dispatch({ type: 'QUEUE_INFO_UPDATED', queueInfo: conv.queueInfo });
      }

      // Typing indicator sync
      if (conv.agentTyping !== this.state.agentTyping) {
        this.dispatch({ type: 'AGENT_TYPING_CHANGED', typing: conv.agentTyping });
      }

      if (conv.isEnded && this.state.session.phase !== 'ended') {
        this.dispatch({
          type: 'SESSION_ENDED',
          reason: 'server',
          systemMessage: conv.endMessage,
        });
      }

      // Activity-meter resetten bij merkbare verandering. Adaptive interval
      // blijft daardoor "snel" zolang er iets gebeurt; valt terug zodra het
      // ~60s stil is geweest.
      const newQueuePos = conv.queueInfo?.position ?? null;
      const operatorJustAttached = !prevOperatorAttached && conv.operator !== null;
      const hasActivity =
        conv.messages.length !== prevMsgCount ||
        conv.agentTyping !== prevTyping ||
        operatorJustAttached ||
        newQueuePos !== prevQueuePos;
      if (hasActivity) this.scheduler.markConvActivity();
    } catch (error) {
      if (error instanceof ChatApiError && error.kind === 'session_expired') {
        this.dispatch({ type: 'SESSION_LOST' });
        return;
      }
      console.warn('[Chat] Poll failed, retrying next tick:', error);
    }
  }

  private classifyError(error: unknown): SessionError {
    if (error instanceof ChatApiError) {
      return error.kind === 'unknown' ? 'unknown' : error.kind;
    }
    return 'unknown';
  }

  private readonly onOnline = (): void => {
    this.dispatch({ type: 'CONNECTION_CHANGED', online: true });
  };

  private readonly onOffline = (): void => {
    this.dispatch({ type: 'CONNECTION_CHANGED', online: false });
  };
}

